import type { ReactNode } from "react";
import { motion } from "motion/react";
import { Link } from "react-router";
import {
  ArrowRight,
  Building2,
  Check,
  Clock3,
  Mail,
  MapPin,
  MonitorSmartphone,
  Phone,
  Search,
  Shield,
} from "lucide-react";
import { legalProfile } from "../legal";
import {
  type LandingPageVariant,
  cityProfiles,
  getCityLandingPath,
  webdesignCityPages,
} from "../local-pages";
import { getAbsoluteUrl } from "../site";
import { Seo } from "./Seo";

type CityLandingPageProps = {
  city: string;
  citySlug: string;
  variant: LandingPageVariant;
};

type ContentSection = {
  icon: ReactNode;
  title: string;
  description: string;
};

type Step = {
  title: string;
  description: string;
};

type FaqEntry = {
  question: string;
  answer: string;
};

type LandingPageContent = {
  title: string;
  description: string;
  path: string;
  keywords: string[];
  badge: string;
  heroTitle: string;
  heroDescription: string;
  reasonsTitle: string;
  reasonsDescription: string;
  reasons: string[];
  benefits: ContentSection[];
  included: string[];
  benefitsDescription: string;
  stepsDescription: string;
  steps: Step[];
  pricingTitle: string;
  pricingDescription: string;
  faq: FaqEntry[];
  ctaTitle: string;
  ctaDescription: string;
  serviceType: string;
  structuredDescription: string;
  offerDescription: string;
};

function buildKeywords(variant: LandingPageVariant, city: string, citySlug: string) {
  const cityKeyword = city.toLocaleLowerCase("de-DE");
  const cityVariants = Array.from(new Set([cityKeyword, citySlug]));

  const terms =
    variant === "webdesign"
      ? ["webdesign", "website", "webdesigner", "webdesign agentur", "homepage"]
      : variant === "webdesigner"
        ? [
            "webdesigner",
            "webdesign",
            "website erstellen",
            "homepage erstellen",
            "webdesign freelancer",
          ]
        : [
            "website erstellen lassen",
            "website erstellen",
            "homepage erstellen lassen",
            "firmenwebsite",
            "website agentur",
          ];

  return terms.flatMap((term) => cityVariants.map((cityVariant) => `${term} ${cityVariant}`));
}

function getWebdesignContent(city: string, citySlug: string): LandingPageContent {
  return {
    title: `Webdesign ${city}`,
    description: `Webdesign für Unternehmen in ${city}: individuelle Website, klare Preisstruktur sowie Hosting, Wartung und laufende Betreuung durch mutzur.`,
    path: `/webdesign-${citySlug}`,
    keywords: buildKeywords("webdesign", city, citySlug),
    badge: `Webdesign für ${city} und Umgebung`,
    heroTitle: `Professionelles Webdesign in ${city}, das zu Ihrem Unternehmen passt`,
    heroDescription: `Wir gestalten Websites für Unternehmen in ${city}, die modern, verständlich und vertrauenswürdig wirken und im Alltag einfach betreut werden können.`,
    reasonsTitle: `Wann Webdesign in ${city} wirklich etwas bringt`,
    reasonsDescription:
      "Nicht jede lokale Landingpage braucht laute Versprechen. Sie muss vor allem erklären, Vertrauen schaffen und den nächsten Schritt klar machen.",
    reasons: [
      "Sie möchten mit Ihrer Website professionell auftreten und Ihr Angebot klar vermitteln.",
      "Ihre aktuelle Seite wirkt veraltet oder hilft bei Anfragen und Vertrieb zu wenig.",
      "Sie wollen Hosting, Technik und Änderungen nicht selbst koordinieren.",
      "Sie möchten zügig live gehen, ohne ein langes Agenturprojekt zu starten.",
    ],
    benefits: [
      {
        icon: <MonitorSmartphone className="h-6 w-6" />,
        title: "Modernes Webdesign",
        description:
          "Eine Website, die zu Ihrem Unternehmen passt, Vertrauen schafft und auf allen Geräten sauber funktioniert.",
      },
      {
        icon: <Search className="h-6 w-6" />,
        title: "Lokale Sichtbarkeit",
        description: `Klare Inhalte, verständliche Seitenstruktur und eine saubere technische Basis für Anfragen aus ${city} und Umgebung.`,
      },
      {
        icon: <Shield className="h-6 w-6" />,
        title: "Wartung inklusive",
        description:
          "Hosting, Updates, Sicherheit und laufende Betreuung übernehmen wir, damit Ihre Website zuverlässig online bleibt.",
      },
    ],
    included: [
      "Individuelles Design statt Template-Massenware",
      "Responsive Umsetzung für Smartphone, Tablet und Desktop",
      "Kontaktmöglichkeiten und klare Conversion-Pfade",
      "Technische Basis für lokale SEO",
      "Hosting, Wartung und Sicherheitsupdates inklusive",
      "Laufende Anpassungen im Rahmen des Pakets",
    ],
    benefitsDescription:
      "Keine lose Übergabe, sondern eine Website, die auch nach dem Launch gepflegt und weitergeführt wird.",
    stepsDescription:
      "Direkt in der Abstimmung, sauber strukturiert und ohne unnötige Projektschleifen.",
    steps: [
      {
        title: "Anfrage senden",
        description:
          "Sie schicken uns Ihre Anfrage und geben uns die wichtigsten Infos zu Unternehmen, Angebot und Zielgruppe mit.",
      },
      {
        title: "Entwurf erhalten",
        description: `Sie erhalten einen ersten Entwurf, der Ihr Angebot verständlich zeigt und auf Ihre Zielgruppe in ${city} zugeschnitten ist.`,
      },
      {
        title: "Feedback & Launch",
        description:
          "Nach Ihrem Feedback setzen wir die Seite final um, veröffentlichen sie und kümmern uns auch danach um den Betrieb.",
      },
    ],
    pricingTitle: `Webdesign für ${city} mit klarer Preisstruktur`,
    pricingDescription:
      "Sie bekommen kein schwer kalkulierbares Agenturpaket, sondern eine klar definierte Leistung mit laufendem Betrieb und Betreuung.",
    faq: [
      {
        question: `Was kostet Webdesign in ${city} bei mutzur?`,
        answer:
          "Für die Website-Erstellung fallen einmalig 500 € an. Hinzu kommen 99 € monatlich für Hosting, Wartung, Sicherheit und laufende Betreuung, jeweils zzgl. gesetzlicher MwSt.",
      },
      {
        question: `Ist das Modell auch für kleinere Unternehmen in ${city} sinnvoll?`,
        answer:
          "Ja. Das Angebot ist bewusst für kleine und mittlere Unternehmen gedacht, die professionell auftreten möchten, aber keine große Agenturstruktur finanzieren wollen.",
      },
      {
        question: "Übernehmt ihr auch Inhalte und spätere Anpassungen?",
        answer:
          "Ja. Wir unterstützen bei Struktur und Umsetzung und übernehmen im laufenden Paket auch spätere Anpassungen an der Website.",
      },
    ],
    ctaTitle: `Sie möchten online professionell auftreten und in ${city} besser gefunden werden?`,
    ctaDescription:
      "Dann starten Sie mit dem Fragebogen. Auf dieser Basis erstellen wir einen ersten Entwurf für Ihre neue Website oder Landingpage.",
    serviceType: "Webdesign",
    structuredDescription: `Webdesign für Unternehmen in ${city} mit klarem Leistungsumfang, Hosting, Wartung und laufender Betreuung.`,
    offerDescription:
      "Einmaliger Setup-Preis für professionelle Websites, laufende Betreuung separat.",
  };
}

function getWebsiteCreationContent(city: string, citySlug: string): LandingPageContent {
  return {
    title: `Website erstellen lassen ${city}`,
    description: `Website erstellen lassen in ${city}: individuelle Unternehmenswebsite mit transparenter Preisstruktur sowie Hosting, Wartung und laufender Betreuung durch mutzur.`,
    path: `/website-erstellen-lassen-${citySlug}`,
    keywords: buildKeywords("website-erstellen-lassen", city, citySlug),
    badge: `Website-Erstellung für ${city} und Umgebung`,
    heroTitle: `Website erstellen lassen in ${city}, mit klarem Prozess und laufender Betreuung`,
    heroDescription: `Wir erstellen Websites für Unternehmen in ${city}, die professionell auftreten wollen und eine zuverlässige Lösung für Launch, Betrieb und spätere Anpassungen suchen.`,
    reasonsTitle: `Wann es sinnvoll ist, eine Website in ${city} erstellen zu lassen`,
    reasonsDescription:
      "Eine gute Website ist nicht nur online, sondern erklärt Ihr Angebot klar, schafft Vertrauen und erleichtert Anfragen.",
    reasons: [
      "Sie möchten eine Website erstellen lassen, die seriös wirkt und Ihr Angebot klar auf den Punkt bringt.",
      "Ihre bestehende Seite ist veraltet, technisch unpraktisch oder bringt zu wenige qualifizierte Anfragen.",
      "Sie wollen die Erstellung nicht selbst koordinieren, sondern einen festen Ansprechpartner für Konzept, Umsetzung und Betrieb.",
      "Sie möchten zügig online gehen, ohne ein langwieriges Agenturprojekt mit unklaren Zusatzkosten zu starten.",
    ],
    benefits: [
      {
        icon: <MonitorSmartphone className="h-6 w-6" />,
        title: "Individuelle Website",
        description:
          "Wir erstellen eine Website, die Ihr Unternehmen professionell zeigt, auf allen Geräten sauber funktioniert und Vertrauen aufbaut.",
      },
      {
        icon: <Search className="h-6 w-6" />,
        title: "Für lokale Anfragen ausgelegt",
        description: `Die Inhalte, Struktur und technische Basis werden so aufgesetzt, dass Ihr Angebot in ${city} klar gefunden und verstanden werden kann.`,
      },
      {
        icon: <Shield className="h-6 w-6" />,
        title: "Betreuung nach dem Launch",
        description:
          "Hosting, Updates, Sicherheit und laufende Änderungen sind Teil des Pakets, damit Ihre Website nicht sich selbst überlassen bleibt.",
      },
    ],
    included: [
      "Individuelle Website statt Standard-Template",
      "Responsive Umsetzung für Smartphone, Tablet und Desktop",
      "Klare Seitenstruktur für Angebote, Leistungen und Anfragen",
      `Saubere technische Basis für lokale Sichtbarkeit in ${city}`,
      "Hosting, Wartung und Sicherheitsupdates inklusive",
      "Laufende Anpassungen im Rahmen des Pakets",
    ],
    benefitsDescription:
      "Keine einmalige Übergabe, sondern eine Website mit laufender Betreuung und technischer Verantwortung.",
    stepsDescription:
      "Direkt in der Abstimmung, sauber strukturiert und ohne unnötige Projektschleifen.",
    steps: [
      {
        title: "Anfrage senden",
        description:
          "Sie schicken uns die wichtigsten Infos zu Unternehmen, Leistungen und Zielgruppe, damit wir die Website sinnvoll aufsetzen können.",
      },
      {
        title: "Entwurf erhalten",
        description: `Sie erhalten einen ersten Entwurf, der auf Ihr Unternehmen in ${city} zugeschnitten ist und Ihr Angebot verständlich präsentiert.`,
      },
      {
        title: "Feedback & Launch",
        description:
          "Nach Ihrem Feedback finalisieren wir die Website, schalten sie live und kümmern uns auch danach um Betrieb und Pflege.",
      },
    ],
    pricingTitle: `Website erstellen lassen in ${city} mit klarer Preisstruktur`,
    pricingDescription:
      "Statt unklar kalkulierbarer Agenturangebote bekommen Sie eine definierte Leistung mit verlässlichem Betrieb und Betreuung.",
    faq: [
      {
        question: `Was kostet es, eine Website in ${city} erstellen zu lassen?`,
        answer:
          "Für die Website-Erstellung fallen einmalig 500 € an. Hinzu kommen 99 € monatlich für Hosting, Wartung, Sicherheit und laufende Betreuung, jeweils zzgl. gesetzlicher MwSt.",
      },
      {
        question: `Ist das Angebot speziell für Unternehmen in ${city}?`,
        answer:
          "Die Inhalte dieser Seite sind auf Unternehmen in der Region zugeschnitten. Das Modell eignet sich aber genauso für Unternehmen an anderen Standorten.",
      },
      {
        question: "Übernehmt ihr auch spätere Änderungen an der Website?",
        answer:
          "Ja. Laufende Anpassungen sind im monatlichen Paket enthalten, sodass Inhalte und kleinere Änderungen nicht jedes Mal separat beauftragt werden müssen.",
      },
    ],
    ctaTitle: `Sie möchten eine Website für Ihr Unternehmen in ${city} erstellen lassen?`,
    ctaDescription:
      "Dann starten Sie mit dem Fragebogen. Auf dieser Basis erstellen wir einen ersten Entwurf für Ihre neue Website oder Landingpage.",
    serviceType: "Website Erstellung",
    structuredDescription: `Website erstellen lassen in ${city} mit klarer Preisstruktur, Hosting, Wartung und laufender Betreuung durch mutzur.`,
    offerDescription:
      "Einmaliger Setup-Preis für die Erstellung einer professionellen Website, laufende Betreuung separat.",
  };
}

function getWebdesignerContent(city: string, citySlug: string): LandingPageContent {
  return {
    title: `Webdesigner ${city}`,
    description: `Webdesigner in ${city} für Unternehmen, die eine klare, seriöse Website mit Hosting, Wartung und laufender Betreuung suchen.`,
    path: `/webdesigner-${citySlug}`,
    keywords: buildKeywords("webdesigner", city, citySlug),
    badge: `Webdesigner für ${city} und Umgebung`,
    heroTitle: `Webdesigner in ${city} für Unternehmen mit klaren Anforderungen`,
    heroDescription: `Wenn Sie keine laute Agenturpräsentation brauchen, sondern eine professionelle Website mit klarer Struktur und verlässlicher Betreuung, ist mutzur Ihr Ansprechpartner für ${city}.`,
    reasonsTitle: `Wann ein Webdesigner in ${city} die bessere Wahl ist`,
    reasonsDescription:
      "Vor allem dann, wenn die Website seriös wirken, verständlich informieren und im Tagesgeschäft unkompliziert weitergeführt werden soll.",
    reasons: [
      "Sie möchten einen festen Ansprechpartner statt eines aufgeblähten Agenturprozesses.",
      "Ihre Website soll Vertrauen schaffen und Leistungen klar erklären, ohne überladen zu wirken.",
      "Sie möchten Technik, Hosting und laufende Pflege nicht selbst koordinieren.",
      "Sie suchen eine pragmatische Lösung, die zügig online geht und sauber betreut wird.",
    ],
    benefits: [
      {
        icon: <MonitorSmartphone className="h-6 w-6" />,
        title: "Klares Design",
        description:
          "Eine Website, die professionell aussieht, auf allen Geräten funktioniert und Ihr Angebot ohne Ablenkung verständlich macht.",
      },
      {
        icon: <Search className="h-6 w-6" />,
        title: "Lokaler Fokus",
        description: `Texte, Struktur und Signale werden so aufgebaut, dass Ihr Unternehmen in ${city} nachvollziehbar gefunden und eingeordnet werden kann.`,
      },
      {
        icon: <Shield className="h-6 w-6" />,
        title: "Verlässliche Betreuung",
        description:
          "Nach dem Launch bleibt die Website nicht liegen. Hosting, Wartung, Sicherheit und Änderungen laufen über einen festen Ansprechpartner.",
      },
    ],
    included: [
      "Individuelle Website mit seriöser Gestaltung",
      "Responsive Umsetzung für Smartphone, Tablet und Desktop",
      "Klare Seitenstruktur für Leistungen, Referenzen und Kontakt",
      `Saubere technische Basis für Anfragen aus ${city} und Umgebung`,
      "Hosting, Wartung und Sicherheitsupdates inklusive",
      "Laufende Anpassungen im monatlichen Paket",
    ],
    benefitsDescription:
      "Kein loses Übergabeprojekt, sondern eine Website mit klarer Verantwortung auch nach dem Livegang.",
    stepsDescription:
      "Schlank in der Abstimmung, klar in den Zuständigkeiten und ohne unnötige Zwischenschritte.",
    steps: [
      {
        title: "Kurz abstimmen",
        description:
          "Sie schicken die wichtigsten Informationen zu Unternehmen, Angebot und Zielgruppe. Daraus entsteht die Grundlage für Struktur und Inhalte.",
      },
      {
        title: "Entwurf prüfen",
        description: `Sie erhalten einen ersten Entwurf, der auf Ihr Unternehmen in ${city} zugeschnitten ist und die Kernaussagen klar transportiert.`,
      },
      {
        title: "Finalisieren & live gehen",
        description:
          "Nach Ihrem Feedback wird die Website veröffentlicht und im Anschluss weiter betreut, statt an Sie zurückdelegiert zu werden.",
      },
    ],
    pricingTitle: `Webdesigner in ${city} mit transparenter Preisstruktur`,
    pricingDescription:
      "Statt eines schwer kalkulierbaren Agenturpakets bekommen Sie eine definierte Umsetzung mit laufendem Betrieb und direkter Betreuung.",
    faq: [
      {
        question: `Was kostet ein Webdesigner in ${city} bei mutzur?`,
        answer:
          "Für die Website-Erstellung fallen einmalig 500 € an. Hinzu kommen 99 € monatlich für Hosting, Wartung, Sicherheit und laufende Betreuung, jeweils zzgl. gesetzlicher MwSt.",
      },
      {
        question: `Ist mutzur eher Freelancer oder Agentur für ${city}?`,
        answer:
          "mutzur arbeitet bewusst schlank und direkt. Für Unternehmen bedeutet das kurze Wege, klare Zuständigkeiten und keine unnötig komplexe Projektstruktur.",
      },
      {
        question: "Können später Inhalte oder Seiten ergänzt werden?",
        answer:
          "Ja. Änderungen und Ergänzungen laufen im Rahmen der laufenden Betreuung mit, sodass die Website aktuell und nutzbar bleibt.",
      },
    ],
    ctaTitle: `Sie suchen einen Webdesigner in ${city}?`,
    ctaDescription:
      "Dann starten Sie mit dem Fragebogen. Auf dieser Basis erstellen wir einen ersten Entwurf für eine Website, die seriös wirkt und im Alltag funktioniert.",
    serviceType: "Webdesign",
    structuredDescription: `Webdesigner für Unternehmen in ${city} mit klarer Preisstruktur, Hosting, Wartung und laufender Betreuung.`,
    offerDescription:
      "Einmaliger Setup-Preis für professionelle Websites mit laufender Betreuung und technischer Verantwortung.",
  };
}

function getContent(variant: LandingPageVariant, city: string, citySlug: string) {
  if (variant === "website-erstellen-lassen") {
    return getWebsiteCreationContent(city, citySlug);
  }

  if (variant === "webdesigner") {
    return getWebdesignerContent(city, citySlug);
  }

  return getWebdesignContent(city, citySlug);
}

export function CityLandingPage({ city, citySlug, variant }: CityLandingPageProps) {
  const content = getContent(variant, city, citySlug);
  const profile = cityProfiles[citySlug];
  const siblingPages = webdesignCityPages.filter((entry) => entry.citySlug !== citySlug).slice(0, 3);

  const relatedLinks =
    variant === "webdesigner"
      ? [
          { to: `/webdesign-${citySlug}`, label: `Mehr zu Webdesign in ${city}` },
          { to: "/preise", label: "Preise ansehen" },
          { to: "/blog/lokale-seo-fuer-kleine-unternehmen", label: "Artikel zu lokaler SEO" },
        ]
      : [
          { to: "/preise", label: "Preise ansehen" },
          { to: "/blog/lokale-seo-fuer-kleine-unternehmen", label: "Artikel zu lokaler SEO" },
          { to: "/standorte", label: "Alle Standorte" },
        ];

  const breadcrumbItems = [
    { name: "Start", item: getAbsoluteUrl("/") },
    { name: "Standorte", item: getAbsoluteUrl("/standorte") },
    { name: content.title, item: getAbsoluteUrl(content.path) },
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: content.title,
      url: getAbsoluteUrl(content.path),
      description: content.structuredDescription,
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbItems.map((entry, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: entry.name,
          item: entry.item,
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "mutzur",
      url: getAbsoluteUrl(content.path),
      telephone: legalProfile.phone,
      email: legalProfile.email,
      address: {
        "@type": "PostalAddress",
        streetAddress: legalProfile.street,
        postalCode: legalProfile.postalCode,
        addressLocality: legalProfile.city,
        addressCountry: legalProfile.country,
      },
      areaServed: [city, ...(profile?.nearbyAreas ?? [])].map((name) => ({
        "@type": "City",
        name,
      })),
      makesOffer: {
        "@type": "Offer",
        priceCurrency: "EUR",
        price: "500",
        description: content.offerDescription,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: content.serviceType,
      name: content.title,
      areaServed: {
        "@type": "City",
        name: city,
      },
      provider: {
        "@type": "Organization",
        name: "mutzur",
        url: getAbsoluteUrl("/"),
      },
      offers: {
        "@type": "Offer",
        priceCurrency: "EUR",
        price: "500",
        description: content.offerDescription,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: content.faq.map((entry) => ({
        "@type": "Question",
        name: entry.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: entry.answer,
        },
      })),
    },
  ];

  return (
    <div className="pt-20">
      <Seo
        title={content.title}
        description={content.description}
        path={content.path}
        keywords={content.keywords}
        structuredData={structuredData}
      />

      <section className="relative overflow-hidden bg-gradient-to-b from-stone-50 via-white to-amber-50/40">
        <div className="absolute inset-0 opacity-60">
          <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-200/30 blur-3xl" />
          <div className="absolute right-0 top-24 h-80 w-80 rounded-full bg-stone-200/40 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <nav aria-label="Breadcrumb" className="mb-8 text-sm text-gray-500">
            <ol className="flex flex-wrap items-center gap-2">
              <li>
                <Link to="/" className="hover:text-black">
                  Start
                </Link>
              </li>
              <li>/</li>
              <li>
                <Link to="/standorte" className="hover:text-black">
                  Standorte
                </Link>
              </li>
              <li>/</li>
              <li className="text-gray-900">{content.title}</li>
            </ol>
          </nav>

          <div className="grid items-start gap-12 lg:grid-cols-[1.2fr_0.8fr]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-sm text-white">
                <MapPin className="h-4 w-4" />
                {content.badge}
              </div>
              <h1 className="mb-6 text-5xl leading-tight lg:text-6xl">{content.heroTitle}</h1>
              <p className="mb-8 max-w-2xl text-xl leading-relaxed text-gray-600">
                {content.heroDescription}
              </p>
              <div className="flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/fragebogen"
                  className="inline-flex items-center justify-center gap-2 bg-black px-8 py-4 text-white transition-all duration-300 group hover:bg-gray-900"
                >
                  Projekt starten
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/angebot"
                  className="inline-flex items-center justify-center gap-2 border-2 border-black px-8 py-4 text-black transition-all duration-300 hover:bg-black hover:text-white"
                >
                  Leistungen ansehen
                </Link>
              </div>
              <div className="mt-8 flex flex-col gap-4 text-sm text-gray-600 sm:flex-row sm:items-center sm:gap-8">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-600" />
                  Einmalig 500 € zzgl. MwSt.
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-600" />
                  99 € monatlich zzgl. MwSt.
                </div>
              </div>
            </motion.div>

            <motion.aside
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="rounded-[2rem] bg-[#171717] p-8 text-white shadow-2xl lg:p-10"
            >
              <div className="mb-8 flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
                  <Building2 className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm text-white/60">Geeignet für</p>
                  <p className="text-lg">{profile?.audienceSummary ?? "KMU und B2B-Unternehmen"}</p>
                </div>
              </div>
              <div className="space-y-4">
                {content.included.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-300" />
                    <p className="text-white/85">{item}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-6">
                <p className="text-sm text-white/60">Sitz</p>
                <p className="mt-2 text-lg">
                  {legalProfile.street}, {legalProfile.postalCode} {legalProfile.city}
                </p>
                <div className="mt-4 space-y-2 text-sm text-white/80">
                  <p className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <a href={`tel:${legalProfile.phone.replace(/\s+/g, "")}`}>{legalProfile.phone}</a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${legalProfile.email}`}>{legalProfile.email}</a>
                  </p>
                </div>
              </div>
            </motion.aside>
          </div>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-16 max-w-3xl text-center"
          >
            <h2 className="mb-6 text-4xl lg:text-5xl">{content.reasonsTitle}</h2>
            <p className="text-xl text-gray-600">{content.reasonsDescription}</p>
          </motion.div>
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2">
            {content.reasons.map((reason, index) => (
              <motion.div
                key={reason}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className="rounded-2xl border border-stone-200 bg-stone-50 p-8"
              >
                <div className="flex items-start gap-4">
                  <div className="mt-1 flex h-8 w-8 items-center justify-center rounded-full bg-black text-sm text-white">
                    {index + 1}
                  </div>
                  <p className="text-lg leading-relaxed text-gray-700">{reason}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-stone-50 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm font-medium uppercase tracking-[0.18em] text-gray-500">
                Regionaler Bezug
              </p>
              <h2 className="mt-4 text-4xl leading-tight lg:text-5xl">
                Was Unternehmen in {city} typischerweise brauchen
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-gray-700">
                {profile?.localChallenge}
              </p>
              <p className="mt-5 text-base leading-relaxed text-gray-600">
                {profile?.localApproach}
              </p>
            </motion.div>

            <div className="grid gap-6 md:grid-cols-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="rounded-3xl bg-white p-8 shadow-sm"
              >
                <h3 className="text-2xl">Typische Unternehmen</h3>
                <ul className="mt-6 space-y-4">
                  {profile?.typicalClients.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="rounded-3xl bg-white p-8 shadow-sm"
              >
                <h3 className="text-2xl">Wichtige Signale</h3>
                <ul className="mt-6 space-y-4">
                  {profile?.localSignals.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-16 max-w-3xl text-center"
          >
            <h2 className="mb-6 text-4xl lg:text-5xl">Was Sie bei mutzur bekommen</h2>
            <p className="text-xl text-gray-600">{content.benefitsDescription}</p>
          </motion.div>
          <div className="grid gap-8 md:grid-cols-3">
            {content.benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="rounded-3xl bg-stone-50 p-8 shadow-sm"
              >
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-black text-white">
                  {benefit.icon}
                </div>
                <h3 className="mb-3 text-2xl">{benefit.title}</h3>
                <p className="leading-relaxed text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#111111] py-24 text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid items-start gap-10 lg:grid-cols-[0.9fr_1.1fr]"
          >
            <div>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm">
                <Clock3 className="h-4 w-4" />
                Schnell live statt monatelang in Abstimmung
              </div>
              <h2 className="mb-6 text-4xl lg:text-5xl">{content.pricingTitle}</h2>
              <p className="text-lg leading-relaxed text-white/75">{content.pricingDescription}</p>
            </div>
            <div className="rounded-3xl bg-white p-8 text-black lg:p-10">
              <div className="mb-8 grid gap-8 sm:grid-cols-2">
                <div>
                  <p className="mb-2 text-sm text-gray-500">Einmalig</p>
                  <p className="mb-3 text-5xl">500 €</p>
                  <p className="text-gray-600">Erstellung, Setup und Livegang Ihrer Website.</p>
                </div>
                <div>
                  <p className="mb-2 text-sm text-gray-500">Monatlich</p>
                  <p className="mb-3 text-5xl">99 €</p>
                  <p className="text-gray-600">
                    Hosting, Wartung, Sicherheit und laufende Anpassungen.
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Alle Preise verstehen sich zzgl. gesetzlicher MwSt. Für Neukunden gilt eine
                Mindestlaufzeit von 12 Monaten.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-stone-50 py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.9fr]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="rounded-3xl bg-white p-8 shadow-sm lg:p-10"
            >
              <p className="text-sm font-medium uppercase tracking-[0.18em] text-gray-500">
                Betreuung für {profile?.regionLabel ?? city}
              </p>
              <h2 className="mt-4 text-4xl leading-tight lg:text-5xl">
                Zusammenarbeit für {city} und Umgebung
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-gray-700">
                mutzur sitzt in Dresden und betreut Unternehmen aus {city} sowie aus angrenzenden
                Orten digital und strukturiert. Wichtig ist dabei weniger die Entfernung als ein
                sauberer Prozess, klare Zuständigkeiten und eine Website, die auch nach dem Launch
                nicht liegen bleibt.
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {[city, ...(profile?.nearbyAreas ?? [])].map((area) => (
                  <span
                    key={area}
                    className="rounded-full bg-stone-100 px-3 py-1 text-sm text-gray-700"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.08 }}
              className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm lg:p-10"
            >
              <h3 className="text-2xl">So läuft das Projekt ab</h3>
              <p className="mt-4 text-base leading-relaxed text-gray-600">{content.stepsDescription}</p>
              <div className="mt-8 space-y-6">
                {content.steps.map((step, index) => (
                  <div key={step.title} className="flex gap-4">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-black text-sm text-white">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="text-lg">{step.title}</h4>
                      <p className="mt-2 text-gray-600">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="rounded-3xl bg-stone-50 p-8"
            >
              <h2 className="text-3xl leading-tight">Weiterführende Inhalte</h2>
              <p className="mt-4 text-gray-600">
                Wer nicht nur eine Website, sondern auch bessere lokale Sichtbarkeit und einen
                sauberen Entscheidungsprozess will, sollte die Grundlagen mitdenken.
              </p>
              <div className="mt-8 space-y-4">
                {relatedLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center justify-between rounded-2xl border border-stone-200 bg-white px-5 py-4 transition-colors hover:border-stone-300 hover:bg-stone-50"
                  >
                    <span>{link.label}</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.08 }}
            >
              <h2 className="mb-8 text-4xl lg:text-5xl">Nahegelegene Seiten</h2>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {siblingPages.map((entry) => (
                  <Link
                    key={entry.citySlug}
                    to={getCityLandingPath(entry.citySlug, entry.variant)}
                    className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm transition-colors hover:border-stone-300 hover:bg-stone-50"
                  >
                    <p className="text-sm uppercase tracking-[0.18em] text-gray-500">
                      {entry.city}
                    </p>
                    <h3 className="mt-3 text-xl">Webdesign {entry.city}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-gray-600">
                      Lokale Seite für Unternehmen aus {entry.city} und Umgebung.
                    </p>
                  </Link>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-stone-50 py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16 text-center"
          >
            <h2 className="mb-6 text-4xl lg:text-5xl">Häufige Fragen</h2>
          </motion.div>
          <div className="space-y-6">
            {content.faq.map((entry, index) => (
              <motion.div
                key={entry.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className="rounded-2xl bg-white p-8 shadow-sm"
              >
                <h3 className="mb-3 text-2xl">{entry.question}</h3>
                <p className="leading-relaxed text-gray-600">{entry.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="mb-6 text-4xl lg:text-5xl">{content.ctaTitle}</h2>
            <p className="mb-8 text-xl leading-relaxed text-gray-600">{content.ctaDescription}</p>
            <Link
              to="/fragebogen"
              className="inline-flex items-center gap-2 bg-black px-8 py-4 text-white transition-all duration-300 group hover:bg-gray-900"
            >
              Anfrage starten
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
